import { browserClock } from '@lw/shared/clock/browser';

/**
 * WebAudio primitives (ARCHITECTURE §11.4): synthesised beeps/tones with 10 ms envelopes, the
 * 3-beep fire alarm, the 2-tone alert, voice callouts (prerecorded buffers when present, speech
 * synthesis as the fallback), vibration, and the iOS audio-session hint. Everything is scheduled
 * on the AudioContext clock; the CueEngine converts server time to audio time.
 */
export interface NodeHandle {
  /** stop with a short gain ramp; returns how much of the node had played (ms) */
  stop(rampMs?: number): number;
  readonly startAt: number;
  readonly durationMs: number;
}

export class AudioEngine {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private voiceBuffers = new Map<string, AudioBuffer>();
  readonly stub: boolean;
  /** test instrumentation: every scheduled sound */
  readonly played: { kind: string; atAudio: number; hz?: number; text?: string }[] = [];
  /**
   * Sticky "a fresh gesture is required" flag. Set whenever the OS suspends/interrupts the context
   * (backgrounding on iOS) and CLEARED ONLY by a successful user-gesture `arm()` — never by the
   * context spontaneously reporting `running` again, because on iOS the context reports running on
   * return while audio is actually dead. This is what makes `armed` honest across rapid tab flips.
   */
  private needsGesture = false;
  private simulatedInterrupt = false;
  /** fired whenever `armed` may have changed (statechange, arm, tryResume) */
  onStateChange: (() => void) | null = null;

  constructor() {
    this.stub = typeof window !== 'undefined' && (window as unknown as { __lwAudioStub?: boolean }).__lwAudioStub === true;
  }

  get armed(): boolean {
    if (this.stub) return !this.simulatedInterrupt;
    return this.ctx !== null && this.ctx.state === 'running' && !this.needsGesture;
  }

  /** Call from a user gesture. Safe to call repeatedly. */
  async arm(): Promise<boolean> {
    if (this.stub) return true;
    if (!this.ctx) {
      const Ctor = (window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext) as typeof AudioContext | undefined;
      if (!Ctor) return false;
      this.ctx = new Ctor({ latencyHint: 'interactive' });
      this.master = this.ctx.createGain();
      this.master.gain.value = 0.9;
      this.master.connect(this.ctx.destination);
      const session = (navigator as unknown as { audioSession?: { type: string } }).audioSession;
      if (session) {
        try {
          session.type = 'playback'; // iOS 16.4+: the ring/silent switch does not mute WebAudio
        } catch {
          // unsupported value
        }
      }
      this.ctx.addEventListener('statechange', () => this.handleStateChange());
      void this.preloadVoices();
    }
    if (this.ctx.state !== 'running') {
      try {
        await this.ctx.resume();
      } catch {
        this.onStateChange?.();
        return false;
      }
    }
    if (this.ctx.state === 'running') {
      this.needsGesture = false; // the gesture worked → audio is live
      this.interruptedSinceArm = false;
    }
    this.onStateChange?.();
    return this.armed;
  }

  /**
   * Attempt to resume without a user gesture — called when the tab becomes visible again. On desktop
   * (background tabs keep audio, or the context resumes without a gesture) this clears the gesture
   * requirement; on iOS the context cannot resume without a touch, so `needsGesture` stays set and
   * the TAP TO ARM gate reappears. Either way `onStateChange` re-renders.
   */
  async tryResume(): Promise<boolean> {
    if (this.stub) return true;
    if (!this.ctx) return false;
    if (this.ctx.state !== 'running') {
      try {
        await this.ctx.resume();
      } catch {
        // needs a gesture (iOS)
      }
    }
    // only a resume that actually succeeded here (desktop) may clear the flag; iOS leaves it set
    if (this.ctx.state === 'running' && !this.needsGesture) {
      // still running, was never flagged → nothing to do
    } else if (this.ctx.state === 'running' && this.interruptedSinceArm) {
      this.needsGesture = true; // iOS reports running on return but audio is dead — force a gesture
    } else if (this.ctx.state !== 'running') {
      this.needsGesture = true;
    }
    this.onStateChange?.();
    return this.armed;
  }

  /** set the moment the context leaves 'running'; a foreground 'running' after this is not trusted. */
  private interruptedSinceArm = false;

  private handleStateChange(): void {
    if (this.ctx && this.ctx.state !== 'running') {
      this.needsGesture = true;
      this.interruptedSinceArm = true;
    }
    this.onStateChange?.();
  }

  /** true if the context exists but the OS suspended/interrupted it (needs a gesture to resume). */
  get interrupted(): boolean {
    return this.stub ? this.simulatedInterrupt : this.ctx !== null && (this.ctx.state !== 'running' || this.needsGesture);
  }

  /** test hook (stub mode only): emulate the OS suspending/resuming the audio context. */
  simulateInterruption(on: boolean): void {
    if (!this.stub) return;
    this.simulatedInterrupt = on;
    this.onStateChange?.();
  }

  /** audio clock seconds */
  now(): number {
    return this.stub ? browserClock.perfNow() / 1000 : (this.ctx?.currentTime ?? 0);
  }

  private async preloadVoices(): Promise<void> {
    if (!this.ctx) return;
    for (const name of ['laser-on', 'laser-off', 'shot-in-flight', 'lasing', 'check-fire', 'cease-lasing']) {
      try {
        const r = await fetch(`/voice/${name}.mp3`);
        if (!r.ok) continue;
        const buf = await this.ctx.decodeAudioData(await r.arrayBuffer());
        this.voiceBuffers.set(name, buf);
      } catch {
        // speech synthesis fallback
      }
    }
  }

  private osc(atAudio: number, durationMs: number, hz: number, gain: number): NodeHandle {
    this.played.push({ kind: 'beep', atAudio, hz });
    if (this.stub || !this.ctx || !this.master || this.ctx.state !== 'running') return { stop: () => 0, startAt: atAudio, durationMs };
    const ctx = this.ctx;
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.type = 'square';
    o.frequency.value = hz;
    const t0 = Math.max(atAudio, ctx.currentTime);
    const t1 = t0 + durationMs / 1000;
    g.gain.setValueAtTime(0.0001, t0);
    g.gain.exponentialRampToValueAtTime(gain, t0 + 0.01);
    g.gain.setValueAtTime(gain, Math.max(t0 + 0.01, t1 - 0.01));
    g.gain.exponentialRampToValueAtTime(0.0001, t1);
    o.connect(g).connect(this.master);
    o.start(t0);
    o.stop(t1 + 0.02);
    let stopped = false;
    return {
      startAt: t0,
      durationMs,
      stop: (rampMs = 20) => {
        if (stopped) return 0;
        stopped = true;
        const now = ctx.currentTime;
        const playedMs = Math.max(0, Math.min(durationMs, (now - t0) * 1000));
        try {
          g.gain.cancelScheduledValues(now);
          g.gain.setValueAtTime(Math.max(g.gain.value, 0.0001), now);
          g.gain.exponentialRampToValueAtTime(0.0001, now + rampMs / 1000);
          o.stop(now + rampMs / 1000 + 0.005);
        } catch {
          // already ended
        }
        return playedMs;
      },
    };
  }

  /** one ramp beep (120 ms) */
  beep(atAudio: number, hz: number, durationMs = 120): NodeHandle {
    return this.osc(atAudio, durationMs, hz, 0.5);
  }

  /** the 3 s mark tone */
  tone(atAudio: number, hz: number, durationMs = 3000): NodeHandle {
    return this.osc(atAudio, durationMs, hz, 0.6);
  }

  /** fire alarm: 3 × 200 ms at 660 Hz */
  alarm(atAudio: number): NodeHandle[] {
    return [0, 0.3, 0.6].map((o) => this.osc(atAudio + o, 200, 660, 0.6));
  }

  /** 2-tone attention alert (shot-late, check-fire) */
  twoTone(atAudio: number): NodeHandle[] {
    return [this.osc(atAudio, 250, 523, 0.6), this.osc(atAudio + 0.3, 400, 784, 0.6)];
  }

  /** voice callout: prerecorded buffer if present, speech synthesis otherwise */
  voice(atAudio: number, name: string, text: string): NodeHandle {
    this.played.push({ kind: 'voice', atAudio, text });
    const delayMs = Math.max(0, (atAudio - this.now()) * 1000);
    const buf = this.voiceBuffers.get(name);
    if (buf && this.ctx && this.master && !this.stub) {
      const src = this.ctx.createBufferSource();
      src.buffer = buf;
      src.connect(this.master);
      const t0 = Math.max(atAudio, this.ctx.currentTime);
      src.start(t0);
      return { startAt: t0, durationMs: buf.duration * 1000, stop: () => { try { src.stop(); } catch { /* ended */ } return 0; } };
    }
    let handle: number | null = null;
    let utter: SpeechSynthesisUtterance | null = null;
    const speak = (): void => {
      if (this.stub || typeof speechSynthesis === 'undefined') return;
      utter = new SpeechSynthesisUtterance(text);
      utter.lang = 'en-US';
      utter.rate = 0.95;
      speechSynthesis.speak(utter);
    };
    if (delayMs < 5) speak();
    else handle = window.setTimeout(speak, delayMs);
    return {
      startAt: atAudio,
      durationMs: 1200,
      stop: () => {
        if (handle !== null) clearTimeout(handle);
        if (utter && typeof speechSynthesis !== 'undefined') speechSynthesis.cancel();
        return 0;
      },
    };
  }

  vibrate(pattern: number | number[]): void {
    try {
      navigator.vibrate?.(pattern);
    } catch {
      // unsupported
    }
  }

  /** the audio self-test tone (§11.4): 1 s at 880 Hz, now */
  testTone(): NodeHandle {
    return this.osc(this.now() + 0.05, 1000, 880, 0.6);
  }
}
